import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { OfflineBanner } from './OfflineBanner'
import { InstallBanner } from './InstallBanner'
import styles from './Layout.module.css'

interface LayoutProps {
  children: React.ReactNode
}

const NAV_ITEMS = [
  { path: '/', label: 'Proyectos', iconD: 'M3 7h18M3 12h18M3 17h18', exact: true },
  { path: '/profile', label: 'Perfil', iconD: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
]

const ADMIN_NAV_ITEM = {
  path: '/admin',
  label: 'Admin',
  iconD: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  exact: false,
}

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { user, logout } = useAuth()

  const visibleNavItems = user?.is_admin
    ? [...NAV_ITEMS, ADMIN_NAV_ITEM]
    : NAV_ITEMS

  return (
    <div className={styles.shell}>
      {/* Sidebar — desktop only */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <Link to="/" className={styles.sidebarLogo} aria-label="Fásico — Inicio">
            <img src="/icono-app-ui.svg" alt="" aria-hidden="true" className={styles.logoIsotipo} />
            <span className={styles.logoText}>Fásico</span>
          </Link>
          <nav className={styles.sidebarNav} aria-label="Navegación principal">
            {visibleNavItems.map((item) => {
              const active = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <NavIcon d={item.iconD} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </div>
        <div className={styles.sidebarUser}>
          <div className={styles.userAvatar}>
            {user?.full_name ? user.full_name[0].toUpperCase() : '?'}
          </div>
          <div className={styles.userInfo}>
            <p className={styles.userName}>{user?.full_name ?? 'Mi cuenta'}</p>
            <p className={styles.userEmail}>{user?.email ?? ''}</p>
          </div>
          <button
            className={styles.logoutBtn}
            onClick={logout}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={styles.main}>
        {user && !user.email_verified && (
          <div className={styles.verificationBanner} role="alert">
            <span aria-hidden="true">⚠️</span>
            <span>Verifica tu email para poder generar documentos.</span>
          </div>
        )}
        {children}
      </main>

      {/* Bottom nav — mobile only */}
      <nav className={styles.bottomNav} aria-label="Navegación móvil">
        {visibleNavItems.map((item) => {
          const active = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.bottomNavItem} ${active ? styles.bottomNavItemActive : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <NavIcon d={item.iconD} />
              <span className={styles.bottomNavLabel}>{item.label}</span>
            </Link>
          )
        })}
      </nav>

      <OfflineBanner />
      <InstallBanner />
    </div>
  )
}
